const answerCards = [
  {
    title: 'What Profit Pulse AI does',
    body: 'Profit Pulse AI turns true order-level profit into a margin exception queue so merchants can review what lost money and why.',
  },
  {
    title: 'Who it is for',
    body: 'It is built for Shopify founders and operators who need a workflow for protecting margin, not another revenue dashboard.',
  },
  {
    title: 'Why teams use it',
    body: 'The app flags negative-margin orders, low-margin products, discount leakage, and shipping drag so merchants know what to fix next.',
  },
];

const featureCards = [
  {
    title: 'True profit per order',
    body: 'Compare revenue to actual retained profit after product costs, shipping, fees, and discounts.',
  },
  {
    title: 'Margin exception queue',
    body: 'Review negative-margin orders, low-margin products, and unreliable profit data in one focused workflow.',
  },
  {
    title: 'Negative-margin order detection',
    body: 'Spot the orders that already leaked profit, then trace whether COGS, discounts, shipping, or fees caused it.',
  },
  {
    title: 'Discount leakage analysis',
    body: 'See how promotions erode margin instead of treating discounting as free growth.',
  },
  {
    title: 'Shipping and fee drag',
    body: 'Track the operational costs that quietly compress margin even when revenue looks healthy.',
  },
  {
    title: 'Founder profit leak report',
    body: 'Export a 90-day summary for weekly operations reviews instead of manually rebuilding margin context.',
  },
];

const faqItems = [
  {
    question: 'What does Profit Pulse AI measure?',
    answer:
      'Profit Pulse AI focuses on true order-level profit and margin exceptions after COGS, shipping, fees, and discounts rather than stopping at revenue.',
  },
  {
    question: 'Who should use Profit Pulse AI?',
    answer:
      'The app is for Shopify merchants and operators who need a repeatable review workflow for negative-margin orders, low-margin products, and operational profit leaks.',
  },
  {
    question: 'Why is this not just another analytics dashboard?',
    answer:
      'The product is organized around exceptions and next actions: what lost money, why it was flagged, and what to review in a weekly profit leak report.',
  },
];

export default function LandingPage() {
  return (
    <main className="pp-marketing">
      <header className="pp-hero">
        <div className="pp-shell pp-hero-grid">
          <div className="pp-hero-copy">
            <div className="pp-badge">Margin exception workflow for Shopify</div>
            <h1>Review negative-margin orders and fix profit leaks.</h1>
            <p className="pp-lead">
              Profit Pulse AI turns true order-level profit after COGS, shipping,
              fees, and discounts into a focused queue of margin exceptions,
              leak causes, and weekly report exports.
            </p>

            <div className="pp-actions">
              <a
                className="pp-button pp-button-primary"
                href="https://apps.shopify.com/profit-pulse-ai"
                target="_blank"
                rel="noopener noreferrer"
              >
                Install on Shopify
              </a>
              <a className="pp-button pp-button-secondary" href="/privacy-policy.html">
                Privacy policy
              </a>
            </div>

            <p className="pp-fineprint">
              Free plan available — no credit card required. Pro adds a 7-day free trial, cancel anytime.
            </p>

            <div className="pp-chips" aria-label="Primary product outcomes">
              <span>True profit per order</span>
              <span>Low-margin product alerts</span>
              <span>Negative-margin orders</span>
              <span>Discount leakage</span>
            </div>
          </div>

          <aside className="pp-surface pp-facts">
            <p className="pp-kicker">Product facts</p>
            <dl className="pp-facts-grid">
              <div>
                <dt>Designed for</dt>
                <dd>Shopify merchants and operators</dd>
              </div>
              <div>
                <dt>Main output</dt>
                <dd>Margin exception queue</dd>
              </div>
              <div>
                <dt>Core workflow</dt>
                <dd>Detect profit leaks, then act on them</dd>
              </div>
              <div>
                <dt>Privacy stance</dt>
                <dd>Browser-first recommendations where possible</dd>
              </div>
            </dl>
          </aside>
        </div>
      </header>

      <section className="pp-section">
        <div className="pp-shell">
          <div className="pp-section-head">
            <div>
              <p className="pp-kicker">Direct answers</p>
              <h2>What Profit Pulse AI is, who it helps, and why it matters.</h2>
            </div>
            <p>
              This copy is intentionally answer-oriented so merchants, reviewers,
              search systems, and answer engines can understand the product without
              inferring its job from dashboard screenshots alone.
            </p>
          </div>

          <div className="pp-grid pp-grid-3">
            {answerCards.map((item) => (
              <article key={item.title} className="pp-surface pp-card">
                <h3>{item.title}</h3>
                <p>{item.body}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="pp-section pp-section-muted">
        <div className="pp-shell">
          <div className="pp-section-head">
            <div>
              <p className="pp-kicker">What it tracks</p>
              <h2>Built for margin exception review, not vanity analytics.</h2>
            </div>
            <p>
              Revenue is easy to see. The harder question is what you actually
              keep after all the costs that compress margin. Profit Pulse AI is
              designed around that question.
            </p>
          </div>

          <div className="pp-grid pp-grid-3">
            {featureCards.map((item) => (
              <article key={item.title} className="pp-surface pp-card">
                <h3>{item.title}</h3>
                <p>{item.body}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="pp-section">
        <div className="pp-shell">
          <div className="pp-section-head">
            <div>
              <p className="pp-kicker">Why merchants install it</p>
              <h2>Margin problems are usually hiding in a few repeatable places.</h2>
            </div>
          </div>

          <div className="pp-grid pp-grid-2">
            <article className="pp-surface pp-story">
              <h3>Revenue can hide bad unit economics.</h3>
              <p>
                A store can look healthy on top-line revenue while shipping drag,
                aggressive discounts, or weak product margins quietly erode what
                the business actually keeps.
              </p>
            </article>
            <article className="pp-surface pp-story">
              <h3>Profit Pulse turns that into a decision workflow.</h3>
              <p>
                Instead of only charting data, the app surfaces negative-margin
                orders, low-margin products, and operational drag as a review queue
                so the merchant can reprioritize the next action.
              </p>
            </article>
          </div>
        </div>
      </section>

      <section className="pp-section pp-section-muted" id="faq">
        <div className="pp-shell">
          <div className="pp-section-head">
            <div>
              <p className="pp-kicker">FAQ</p>
              <h2>Common questions about the product.</h2>
            </div>
          </div>

          <div className="pp-faq">
            {faqItems.map((item, index) => (
              <article key={item.question} className="pp-surface pp-faq-item">
                <div className="pp-faq-index">{String(index + 1).padStart(2, '0')}</div>
                <div>
                  <h3>{item.question}</h3>
                  <p>{item.answer}</p>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="pp-section">
        <div className="pp-shell">
          <div className="pp-surface pp-cta">
            <div>
              <p className="pp-kicker">Outcome</p>
              <h2>Turn hidden margin leaks into a weekly review workflow.</h2>
              <p>
                Profit Pulse AI helps merchants move from top-line revenue
                reporting to concrete margin exceptions, report exports, and
                next actions.
              </p>
            </div>
            <div className="pp-actions">
              <a
                className="pp-button pp-button-primary"
                href="https://apps.shopify.com/profit-pulse-ai"
                target="_blank"
                rel="noopener noreferrer"
              >
                Install on Shopify
              </a>
              <a className="pp-button pp-button-secondary" href="/screenshots.html">
                View screenshots
              </a>
            </div>
            <p className="pp-fineprint">
              Start free — no credit card required.
            </p>
          </div>
        </div>
      </section>
    </main>
  );
}
